import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';
import { useOrgStore } from '@/stores/org';
import { usePortalStore } from '@/stores/portal';
import { getOrgRoutes, getTopLevelRoutes } from '@/modules';
import i18n from '@/i18n';
import { isCustomDomain, customDomainOrgSlug, platformUrl } from '@/services/customDomain';
import { setupAxiosInterceptor, clearToken, createExchangeCode } from '@/services/oauthClient';
import { hasOpenFormModal, closeAllModals } from '@/utils/modalState';
import { hasAnyFormModalDirty } from '@/composables/useModalDirty';
import { installModuleEntryTracker } from '@/composables/useModuleEntry';

// Eager-load auth pages so navigation between them is instant
import LoginIndex from './pages/login/Index.vue';
import LoginPage from './pages/login/Login.vue';
import LostPassword from './pages/login/LostPassword.vue';
import ResetPassword from './pages/login/ResetPassword.vue';
import RegisterPage from './pages/login/Register.vue';
import Google2Fa from './pages/login/Google2Fa.vue';
import { listBootstrapFor, primeListBootstrap } from './utils/listBootstrap';

/** The portal screens a prospect reaches — see the guard below. */
const PROSPECT_PORTAL_ROUTES = new Set([
    'portal-dashboard',
    'portal-profile',
    'portal-quotes',
    'portal-quote-detail',
    'portal-contracts',
    'portal-contract-detail',
    'portal-document-requests',
    'portal-document-request-detail'
]);

const routes = [
    {
        path: '/',
        name: 'root',
        component: { render: () => null }
    },
    {
        path: '/app',
        name: 'app',
        // Redirect handled dynamically in beforeEach (no static redirect)
        component: { render: () => null }
    },
    {
        path: '/login/index',
        component: LoginIndex,
        children: [
            {
                path: '/login',
                name: 'login',
                component: LoginPage
            },
            {
                path: '/lost-password',
                name: 'lost-password',
                component: LostPassword
            },
            {
                path: '/lost-password/:token',
                name: 'lost-password-reset',
                component: ResetPassword,
                props: true
            },
            {
                path: '/register',
                name: 'register',
                component: RegisterPage
            },
            {
                path: '/2fa',
                name: '2fa',
                component: Google2Fa
            }
        ]
    },

    {
        path: '/oauth/authorize',
        name: 'oauth-authorize',
        component: () => import('./pages/login/OAuthAuthorize.vue')
    },

    {
        path: '/accept-invitation/:id(\\d+)/:token',
        name: 'accept-invitation',
        component: () => import('./pages/login/AcceptInvitation.vue')
    },

    // Platform — profile, organizations, dashboard, admin
    {
        path: '/platform',
        name: 'platform',
        component: () => import('./pages/AppLayout.vue'),
        redirect: { name: 'platform-dashboard' },
        meta: { navType: 'platform' },
        children: [
            {
                path: 'dashboard',
                name: 'platform-dashboard',
                component: () => import('./pages/platform/Dashboard.vue')
            },

            // Personal settings
            {
                path: 'profile',
                name: 'platform-profile',
                component: () => import('./pages/platform/Profile.vue')
            },
            {
                path: 'security',
                name: 'platform-security',
                component: () => import('./pages/platform/Security.vue')
            },
            {
                path: 'notifications',
                name: 'platform-notification-settings',
                component: () => import('./pages/platform/NotificationSettings.vue')
            },
            {
                path: 'devices',
                name: 'platform-devices',
                component: () => import('./pages/platform/devices/Index.vue')
            },
            {
                path: 'activity',
                name: 'platform-activity',
                component: () => import('./pages/platform/activity/Index.vue')
            },
            {
                path: 'authorized-apps',
                name: 'platform-authorized-apps',
                component: () => import('./pages/platform/authorized-apps/Index.vue')
            },
            {
                path: 'oauth-apps',
                name: 'platform-oauth-apps',
                component: () => import('./pages/platform/oauth-apps/Index.vue')
            },
            {
                path: 'personal-tokens',
                name: 'platform-personal-tokens',
                component: () => import('./pages/platform/personal-tokens/Index.vue')
            },

            // My organizations
            {
                path: 'organizations',
                name: 'platform-organizations',
                component: () => import('./pages/platform/organization/Index.vue')
            },
            {
                path: 'organizations/new',
                name: 'platform-new-organization',
                component: () => import('./pages/platform/organization/New.vue')
            },
            {
                path: 'organizations/:id/delete',
                name: 'platform-delete-organization',
                component: () => import('./pages/platform/organization/Delete.vue'),
                props: true
            },

            // Administration (platform roles only)
            {
                path: 'admin/organizations',
                name: 'platform-admin-organizations',
                component: () => import('./pages/platform/organizations/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-org-preview',
                        component: () => import('./pages/platform/organizations/Preview.vue'),
                        redirect: { name: 'platform-admin-org-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-org-overview',
                                component: () => import('./pages/platform/organizations/preview/Overview.vue'),
                                props: true
                            },
                            {
                                path: 'metrics',
                                name: 'platform-admin-org-metrics',
                                component: () => import('./pages/platform/organizations/preview/Metrics.vue'),
                                props: true
                            },
                            {
                                path: 'subscription',
                                name: 'platform-admin-org-subscription',
                                component: () => import('./pages/platform/organizations/preview/Subscription.vue'),
                                props: true
                            },
                            {
                                path: 'modules',
                                name: 'platform-admin-org-modules',
                                component: () => import('./pages/platform/organizations/preview/Modules.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/usage-plans',
                name: 'platform-admin-usage-plans',
                component: () => import('./pages/platform/usage-plans/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-usage-plan-preview',
                        component: () => import('./pages/platform/usage-plans/Preview.vue'),
                        redirect: { name: 'platform-admin-usage-plan-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-usage-plan-overview',
                                component: () => import('./pages/platform/usage-plans/preview/Overview.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/license-types',
                name: 'platform-admin-license-types',
                component: () => import('./pages/platform/license-types/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-license-type-preview',
                        component: () => import('./pages/platform/license-types/Preview.vue'),
                        redirect: { name: 'platform-admin-license-type-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-license-type-overview',
                                component: () => import('./pages/platform/license-types/preview/Overview.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/plans',
                name: 'platform-admin-plans',
                component: () => import('./pages/platform/plans/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-plan-preview',
                        component: () => import('./pages/platform/plans/Preview.vue'),
                        redirect: { name: 'platform-admin-plan-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-plan-overview',
                                component: () => import('./pages/platform/plans/preview/Overview.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/signature-claims',
                name: 'platform-admin-signature-claims',
                component: () => import('./pages/platform/signature-claims/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-signature-claim-preview',
                        component: () => import('./pages/platform/signature-claims/Preview.vue'),
                        redirect: { name: 'platform-admin-signature-claim-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-signature-claim-overview',
                                component: () => import('./pages/platform/signature-claims/preview/Overview.vue'),
                                props: true
                            },
                            {
                                path: 'history',
                                name: 'platform-admin-signature-claim-history',
                                component: () => import('./pages/platform/signature-claims/preview/History.vue'),
                                props: true
                            },
                            {
                                path: 'confirmations',
                                name: 'platform-admin-signature-claim-confirmations',
                                component: () => import('./pages/platform/signature-claims/preview/Confirmations.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/a2p',
                name: 'platform-admin-a2p',
                component: () => import('./pages/platform/a2p/Review.vue'),
                meta: { requiresPlatformRole: true }
            },
            {
                path: 'admin/billing',
                name: 'platform-admin-billing',
                component: () => import('./pages/platform/billing/Billing.vue'),
                meta: { requiresPlatformRole: true }
            },
            {
                path: 'admin/usage',
                name: 'platform-admin-usage',
                component: () => import('./pages/platform/usage/Usage.vue'),
                meta: { requiresPlatformRole: true }
            },
            {
                path: 'admin/database-hosts',
                name: 'platform-admin-database-hosts',
                component: () => import('./pages/platform/database-hosts/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-database-host-preview',
                        component: () => import('./pages/platform/database-hosts/Preview.vue'),
                        redirect: { name: 'platform-admin-database-host-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-database-host-overview',
                                component: () => import('./pages/platform/database-hosts/preview/Overview.vue'),
                                props: true
                            },
                            {
                                path: 'stats',
                                name: 'platform-admin-database-host-stats',
                                component: () => import('./pages/platform/database-hosts/preview/Stats.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/system-project-templates',
                name: 'platform-admin-system-project-templates',
                component: () => import('./pages/platform/system-project-templates/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-system-project-templates-preview',
                        component: () => import('./pages/platform/system-project-templates/Preview.vue'),
                        redirect: { name: 'platform-admin-system-project-templates-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-system-project-templates-overview',
                                component: () =>
                                    import('./pages/platform/system-project-templates/preview/Overview.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/system-automation-templates',
                name: 'platform-admin-system-automation-templates',
                component: () => import('./pages/platform/system-automation-templates/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-system-automation-templates-preview',
                        component: () => import('./pages/platform/system-automation-templates/Preview.vue'),
                        redirect: { name: 'platform-admin-system-automation-templates-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-system-automation-templates-overview',
                                component: () =>
                                    import('./pages/platform/system-automation-templates/preview/Overview.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/signature-catalog/new',
                name: 'platform-admin-signature-catalog-create',
                component: () => import('./pages/platform/signature-catalog/Create.vue'),
                meta: { requiresPlatformRole: true }
            },
            {
                path: 'admin/signature-catalog',
                name: 'platform-admin-signature-catalog',
                component: () => import('./pages/platform/signature-catalog/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-signature-catalog-preview',
                        component: () => import('./pages/platform/signature-catalog/Preview.vue'),
                        redirect: { name: 'platform-admin-signature-catalog-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-signature-catalog-overview',
                                component: () => import('./pages/platform/signature-catalog/preview/Overview.vue'),
                                props: true
                            },
                            {
                                // The designer is a full-screen modal now, not a
                                // tab. The route stays as a redirect so links
                                // already shared still land in it.
                                path: 'designer',
                                name: 'platform-admin-signature-catalog-designer',
                                redirect: to => ({
                                    name: 'platform-admin-signature-catalog-overview',
                                    params: to.params,
                                    query: { designer: '1' }
                                })
                            }
                        ]
                    }
                ]
            },
            {
                path: 'admin/users',
                name: 'platform-admin-users',
                component: () => import('./pages/platform/users/List.vue'),
                meta: { requiresPlatformRole: true },
                children: [
                    {
                        path: ':id(\\d+)',
                        name: 'platform-admin-user-preview',
                        component: () => import('./pages/platform/users/Preview.vue'),
                        redirect: { name: 'platform-admin-user-overview' },
                        props: true,
                        children: [
                            {
                                path: '',
                                name: 'platform-admin-user-overview',
                                component: () => import('./pages/platform/users/preview/Overview.vue'),
                                props: true
                            },
                            {
                                path: 'activity',
                                name: 'platform-admin-user-activity',
                                component: () => import('./pages/platform/users/preview/Activity.vue'),
                                props: true
                            }
                        ]
                    }
                ]
            }
        ]
    },

    // Legacy redirects for old profile paths
    {
        path: '/profile',
        redirect: { name: 'platform-organizations' }
    },
    {
        path: '/profile/organizations',
        redirect: { name: 'platform-organizations' }
    },
    {
        path: '/profile/organization/new',
        redirect: { name: 'platform-new-organization' }
    },

    // Organization routes — assembled from modules
    {
        path: '/organization/:org',
        component: () => import('./pages/AppLayout.vue'),
        redirect: { name: 'dashboard' },
        props: true,
        meta: { navType: 'app' },
        children: [
            // The inbox belongs to no module: it spans every one of them, and
            // lives under {org} because opening a notification needs that context.
            {
                path: 'notifications',
                name: 'notifications',
                component: () => import('./pages/organization/notifications/Index.vue')
            },
            // Where a suspended organization lands: only its billing is open.
            {
                path: 'suspended',
                name: 'organization-suspended',
                component: () => import('./pages/organization/suspended/Index.vue')
            },
            ...getOrgRoutes()
        ]
    },

    // Contract signing (public, no auth)
    {
        path: '/signing/:org/:token',
        name: 'contract-signing',
        component: () => import('@modules/Contracts/Resources/js/pages/signing/Index.vue'),
        props: true,
        meta: { public: true }
    },

    // Public form submission (no auth)
    {
        path: '/:org/f/:token',
        name: 'public-form',
        component: () => import('./pages/public/FormSubmit.vue'),
        props: true,
        meta: { public: true }
    },

    // Public survey submission (no auth)
    {
        path: '/:org/s/:token',
        name: 'public-survey',
        component: () => import('./pages/public/SurveySubmit.vue'),
        props: true,
        meta: { public: true }
    },

    // Public ticket submission (no auth)
    {
        path: '/:org/t/:token',
        name: 'public-ticket',
        component: () => import('./pages/public/TicketSubmit.vue'),
        props: true,
        meta: { public: true }
    },

    // Calendar RSVP (public - token is auth)
    {
        path: '/:org/rsvp/:token',
        name: 'calendar-rsvp',
        component: () => import('./pages/public/CalendarRsvp.vue'),
        props: true,
        meta: { public: true }
    },

    // Top-level module routes (portal, etc.)
    ...getTopLevelRoutes(),

    // Catch-all 404 — must be LAST so named routes are matched first
    {
        path: '/:pathMatch(.*)*',
        name: 'notfound',
        component: () => import('./pages/NotFound.vue')
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

// Set up axios interceptor (session cookie handles auth on all domains)
setupAxiosInterceptor();

// Clear stale bearer tokens from old OAuth-based auth flow
clearToken();

router.beforeEach(async (to, from) => {
    const userStore = useUserStore();

    // Verify if the user has access to the organization id
    const hasAccess = route => {
        if (route.params && route.params.org) {
            // Platform role holders can access any organization
            if (userStore.hasPlatformRole) return true;

            const idx = userStore.organizations.findIndex(o => o.slug === route.params.org);
            if (idx === -1) {
                return false;
            }
        }
        return true;
    };

    const publicPaths = ['/login', '/register', '/2fa'];
    const isPublicPath =
        publicPaths.includes(to.path) ||
        /^\/(lost-password|accept-invitation|portal|signing)(.*)?$/.test(to.path) ||
        to.meta?.public;

    // Block registration when disabled
    if (to.path === '/register' && !window.config?.registrationEnabled) {
        return { name: 'login' };
    }

    // Redirect authenticated users away from login to smart redirect
    if (to.path === '/login' || to.path === '/register') {
        if (userStore.isAuthenticated) {
            return { name: 'app' };
        }
        // Check if there's an existing session (user navigated directly to /login)
        if (!userStore.isAuthenticated) {
            try {
                const response = await axios.get('/api/me');
                userStore.setUser(response.data);
                return { name: 'app' };
            } catch {
                // Not authenticated — continue to login page
            }
        }
    }

    // On custom domain, block access to other organizations
    if (isCustomDomain && to.params?.org && to.params.org !== customDomainOrgSlug) {
        return { name: 'dashboard', params: { org: customDomainOrgSlug } };
    }

    // On custom domain, redirect root, login, register, notfound to org dashboard or bridge login
    if (
        isCustomDomain &&
        (to.name === 'root' || to.name === 'notfound' || ['/login', '/register', '/lost-password'].includes(to.path))
    ) {
        if (userStore.isAuthenticated) {
            return { name: 'dashboard', params: { org: customDomainOrgSlug } };
        }
        // Redirect to main domain bridge for login
        const returnUrl = window.location.origin + '/organization/' + customDomainOrgSlug + '/dashboard';
        window.location.href = `${platformUrl}/auth/bridge?return_url=${encodeURIComponent(returnUrl)}`;
        return false;
    }

    if (to.name === 'notfound') {
        return true;
    }

    // Both domains: session + Passport cookie (CreateFreshApiToken) handles auth automatically
    // The JWT cookie is sent by the browser with every request — no bearer token needed
    if (!userStore.isAuthenticated && !isPublicPath) {
        try {
            const response = await axios.get('/api/me');
            userStore.setUser(response.data);

            if (response.data.language) {
                i18n.global.locale.value = response.data.language;
                window.config.locale = response.data.language;
                moment.locale(response.data.language);
            }

            if (!hasAccess(to)) {
                if (isCustomDomain) {
                    return { name: 'dashboard', params: { org: customDomainOrgSlug } };
                }
                return { name: 'platform-organizations' };
            }
        } catch (error) {
            if (isCustomDomain) {
                // No session on custom domain — redirect to main domain bridge for login
                const returnUrl = window.location.origin + to.fullPath;
                window.location.href = `${platformUrl}/auth/bridge?return_url=${encodeURIComponent(returnUrl)}`;
            } else {
                localStorage.setItem('redirectAfterLogin', to.fullPath);
                window.location.href = '/login';
            }
            return false;
        }
    }

    if (!isPublicPath && !hasAccess(to)) {
        if (isCustomDomain) {
            return { name: 'dashboard', params: { org: customDomainOrgSlug } };
        }
        return { name: 'platform-organizations' };
    }

    // Smart redirect for / and /app
    if ((to.name === 'app' || to.name === 'root') && userStore.isAuthenticated) {
        if (isCustomDomain) {
            return { name: 'dashboard', params: { org: customDomainOrgSlug } };
        }
        if (userStore.organizations.length > 1) {
            return { name: 'platform-dashboard' };
        }
        if (userStore.organizations.length === 1) {
            const org = userStore.organizations[0];
            if (org.custom_domain) {
                window.location.href = `https://${org.custom_domain}/app`;
                return false;
            }
            return { name: 'dashboard', params: { org: org.slug } };
        }
        return { name: 'platform-new-organization' };
    }

    // On custom domain, redirect platform routes to main platform domain with session exchange
    if (isCustomDomain && to.path.startsWith('/platform')) {
        try {
            const code = await createExchangeCode();
            window.location.href = `${platformUrl}/auth/exchange?code=${encodeURIComponent(code)}&redirect=${encodeURIComponent(to.fullPath)}`;
        } catch {
            window.location.href = `${platformUrl}${to.fullPath}`;
        }
        return false;
    }

    // Guard platform admin routes — require platform role
    if (to.meta.requiresPlatformRole && !userStore.hasPlatformRole) {
        return { name: 'platform-dashboard' };
    }

    // Module-based route guard — a module the organization's plan does not
    // include has no reachable screen. The route table itself stays whole: it is
    // built once at boot, before any org is known, so the decision belongs here,
    // where the org of the navigation is known. Enforced only once the module
    // list is loaded — for a member `setOrg()` fills it synchronously from
    // /api/me, so this is already true; the wait only affects platform staff
    // visiting an org they do not belong to.
    // The portal answers the same question from its own session: its visitor is
    // a contact, not a member, so the module list comes from /auth/me and lives
    // in the portal store. Without this, a bookmarked or emailed link to a
    // screen the organization does not hold opens and then paints the 403 the
    // `module:` middleware returns.
    // A prospect's portal is what is addressed to them — their quotes, what they
    // sign or provide, their profile. A client's screen reached by a pasted
    // link would only paint the 403 its endpoint answers, so it is not opened.
    if (to.path.startsWith('/portal/') && to.name && to.name !== 'portal-login') {
        const portalStore = usePortalStore();

        // A pasted link runs this before the layout has read the stored
        // session, which would leave every visitor unknown — and let through.
        if (portalStore.orgSlug !== to.params.org) {
            portalStore.init(to.params.org);
        }
        await portalStore.whenSessionReady();

        if (portalStore.isProspectOnly && !PROSPECT_PORTAL_ROUTES.has(to.name)) {
            return { name: 'portal-dashboard', params: { org: to.params.org } };
        }
    }

    if (to.meta.module && to.path.startsWith('/portal/')) {
        const portalStore = usePortalStore();
        await portalStore.whenSessionReady();

        // isModuleEnabled is permissive while the list is unknown — before the
        // first /auth/me of a fresh magic-link entry. The server gate still has
        // the last word.
        if (!portalStore.isModuleEnabled(to.meta.module)) {
            return { name: 'portal-dashboard', params: { org: to.params.org } };
        }

        return true;
    }

    if (to.meta.module && to.params.org) {
        const orgStore = useOrgStore();

        // On a cold entry — a pasted link, a bookmark, a new tab — the org
        // context is hydrated by AppLayout, which mounts *after* this guard. The
        // module list would still be empty here and the screen would open, then
        // sit empty behind a 403. /api/me has already been awaited above and
        // carries `enabled_modules` for every organization the person belongs
        // to, so the answer is on hand without a request.
        if (!orgStore.modulesLoaded) {
            const org = userStore.organizations?.find(o => o.slug === to.params.org);
            if (org) {
                orgStore.setOrg(org);
                orgStore.setApiBase(`/api/${org.slug}`);
            }
        }

        // Still unloaded means platform staff visiting an organization they do
        // not belong to: it is absent from their /api/me, and the server gate
        // has the last word anyway.
        if (orgStore.modulesLoaded && !orgStore.isModuleEnabled(to.meta.module)) {
            return { name: 'dashboard', params: { org: to.params.org } };
        }
    }

    // Ability-based route guard — redirect to dashboard if user lacks section access
    // Only enforced once roles are loaded (permissive until then, backend still enforces)
    if (to.meta.ability && to.params.org) {
        const orgStore = useOrgStore();
        if (orgStore.rolesLoaded && !orgStore.canAny(to.meta.ability)) {
            return { name: 'dashboard', params: { org: to.params.org } };
        }
    }

    return true;
});

// A form modal belongs to the page it was opened from, so navigating away
// closes it — after a warning when there is something to lose.
let _modalLeaveConfirming = false;
router.beforeEach(async () => {
    if (!hasOpenFormModal() || _modalLeaveConfirming) return true;

    // Nothing typed, nothing to lose. Left open it would hang over the page
    // that was just navigated to, which is what a link inside a modal used to
    // do — and the only way out was the modal's own Cancel.
    if (!hasAnyFormModalDirty()) {
        closeAllModals();

        return true;
    }

    _modalLeaveConfirming = true;
    try {
        await Modal.confirm(
            i18n.global.t('Unsaved changes'),
            i18n.global.t('You have unsaved changes. If you leave, your changes will be lost.')
        );
        closeAllModals();
        return true;
    } catch {
        return false;
    } finally {
        _modalLeaveConfirming = false;
    }
});

// A list's opening requests leave in one, while its page code is still
// downloading — last, so only a navigation every guard let through asks.
router.beforeEach(to => {
    const spec = listBootstrapFor(to);
    if (spec) {
        primeListBootstrap(to.params.org, spec);
    }
});

// Track link-vs-back module entries so previews reset to the overview tab in
// read mode on a fresh (link) entry, but keep their last tab on history.back.
installModuleEntryTracker(router);

export default router;
